'use client';

import { useState, useRef, useEffect } from 'react';
import { useAuthContext } from '@/components/AuthProvider';
import { useModal } from '@/components/ModalProvider';
import { useLanguage } from '@/components/LanguageProvider'; 
import Image from 'next/image';
import Link from 'next/link';
import { Plus, LogOut, Settings, LayoutDashboard, Copy, Check, ChevronDown, MessageCircle } from 'lucide-react';
import InboxDropdown from './InboxDropdown'; 

const LOCAL_API_URL = process.env.NEXT_PUBLIC_LOCAL_API || 'https://sg-public-api.royolands.com/rpweb';

interface UserNavProps {
  mode?: 'default' | 'hero';
}

export default function UserNav({ mode = 'default' }: UserNavProps) {
  // ДОБАВЛЕН openLoginModal ИЗ КОНТЕКСТА
  const { user, isAuthenticated, isLoading, logout, openLoginModal } = useAuthContext();
  const { openDeposit } = useModal();
  const { t } = useLanguage(); 

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadCount = useRef(0);
  const isFirstCheck = useRef(true); 
  
  const containerRef = useRef<HTMLDivElement>(null);

  const isHero = mode === 'hero';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setIsInboxOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkUnreadNotifications = async () => {
    if (!user?.oauthId) return;
    try {
      const res = await fetch(`${LOCAL_API_URL}/api/inbox/notifications/${user.oauthId}/unread-count`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();

        if (!isFirstCheck.current && data.count > prevUnreadCount.current) {
          const audio = new Audio('/sounds/pop.mp3');
          audio.volume = 0.5;
          audio.play().catch(e => console.log("Браузер заблокировал автовоспроизведение звука", e));
        }

        isFirstCheck.current = false;
        prevUnreadCount.current = data.count;
        setUnreadCount(data.count);
      }
    } catch (e) {
      console.error("Ошибка проверки уведомлений:", e);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.oauthId) {
      checkUnreadNotifications();
      const interval = setInterval(checkUnreadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, user?.oauthId]);

  const copyId = () => {
    if (user?.playerId) {
      navigator.clipboard.writeText(user.playerId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleMarkAsReadSuccess = () => {
    setUnreadCount(0);
    prevUnreadCount.current = 0;
  };

  const toggleInbox = () => {
    setIsInboxOpen(!isInboxOpen);
    setIsDropdownOpen(false);
  };

  const toggleProfile = () => {
    setIsDropdownOpen(!isDropdownOpen);
    setIsInboxOpen(false);
  };

  // 1. ЕСЛИ ИДЕТ ЗАГРУЗКА
  if (isLoading) {
    return (
      <div 
        className={`${isHero ? 'w-14 h-14 rounded-lg' : 'w-10 h-10 rounded-full'} animate-pulse bg-[#151515]`}
      />
    );
  }

  // 2. ЕСЛИ НЕ АВТОРИЗОВАН — КОРРЕКТНЫЙ ВЫЗОВ АВТОРИЗАЦИИ
  if (!isAuthenticated || !user) {
    return (
      <button
        type="button"
        onClick={openLoginModal}
        className={`bg-transparent border-none p-0 outline-none text-white hover:opacity-65 transition-opacity cursor-pointer select-none font-medium ${
          isHero ? 'text-[22px] font-bold' : 'text-[18px]'
        }`}
      >
        {t.userNav.login}
      </button>
    ); 
  }

  // 3. ЕСЛИ АВТОРИЗОВАН
  return (
    <div className={`flex items-center ${isHero ? 'gap-0' : 'gap-5 sm:gap-6'}`} ref={containerRef}>
      
      {/* Кнопка уведомлений (скрыта в hero) */}
      {!isHero && (
        <div className="relative flex items-center">
          <button 
            onClick={toggleInbox} 
            className="relative flex items-center justify-center text-white transition-opacity duration-200 hover:opacity-65 outline-none"
          >
            <MessageCircle size={24} strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center w-[18px] h-[18px] bg-red-500 rounded-full border-[2px] border-[#111111] text-[9px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <div className={`absolute top-full right-0 mt-5 transition-all duration-200 z-50 ${isInboxOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
             <InboxDropdown 
               isOpen={isInboxOpen} 
               onClose={() => setIsInboxOpen(false)} 
               user={user} 
               onMarkAsReadSuccess={handleMarkAsReadSuccess}
             />
          </div>
        </div>
      )}

      {/* Кнопка баланса (скрыта в hero) */}
      {!isHero && (
        <button 
          onClick={openDeposit} 
          className="hidden sm:flex items-center gap-3 bg-[#151515] border border-[#252525] hover:border-purple-500/50 rounded-full pl-4 pr-1.5 py-1.5 transition-all group"
        >
          <div className="flex flex-col items-start leading-none">
            <span className="text-[10px] text-zinc-500 font-bold uppercase">{t.userNav.balance}</span>
            <span className="text-sm font-bold text-white">{user.crystals?.toLocaleString()} 💎</span>
          </div>
          <div className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-white shadow-lg shadow-purple-900/20 group-hover:scale-110 transition-transform">
            <Plus size={14} strokeWidth={3} />
          </div>
        </button>
      )}

      {/* Меню профиля */}
      <div className="relative">
        <div 
          onClick={toggleProfile}
          className={`flex items-center cursor-pointer transition-all select-none ${
            isHero 
              ? 'p-0 hover:scale-105' 
              : 'gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-[#151515] border border-transparent hover:border-[#252525]'
          }`}
        >
          <Image 
            src={`https://sg-public-api.royolands.com/rpweb/morda/${user.nickname}`} 
            width={isHero ? 56 : 38} 
            height={isHero ? 56 : 38} 
            alt="Avatar" 
            className={`bg-[#222] transition-transform ${
              isHero 
                ? 'w-14 h-14 rounded-lg object-cover shadow-none' 
                : 'rounded-full'
            }`} 
            unoptimized
          />
          {!isHero && (
            <ChevronDown size={14} className={`text-zinc-500 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          )}
        </div>

        <div className={`
          absolute top-full right-0 mt-4 w-72 bg-[#0c0c0c] border border-[#1f1f1f] rounded-2xl shadow-2xl p-4 z-50 origin-top-right transition-all duration-200
          ${isDropdownOpen ? 'opacity-100 scale-100 translate-y-0 visible' : 'opacity-0 scale-95 -translate-y-2 invisible'}
        `}>
          <div className="flex items-center gap-3 mb-4">
            <Image src={`https://sg-public-api.royolands.com/rpweb/morda/${user.nickname}`} width={48} height={48} alt="Skin" className="rounded-xl bg-[#222]" unoptimized />
            <div className="overflow-hidden">
              <div className="font-bold text-white truncate">{user.nickname}</div>
              <button onClick={copyId} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white transition-colors">
                #{user.playerId} {copied ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
              </button>
            </div>
          </div>

          <button 
            onClick={() => { openDeposit(); setIsDropdownOpen(false); }} 
            className="sm:hidden w-full mb-3 p-3 rounded-xl bg-[#151515] border border-[#252525] flex items-center justify-between"
          >
            <span className="text-sm font-bold text-white">{user.crystals?.toLocaleString()} 💎</span>
            <div className="bg-purple-600 rounded-full p-1"><Plus size={12} /></div>
          </button>

          <div className="space-y-1">
            <Link href="/me" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#151515] text-zinc-400 hover:text-white transition-colors text-sm font-medium">
              <LayoutDashboard size={16} /> {t.userNav.dashboard}
            </Link>
            <Link href="/settings" onClick={() => setIsDropdownOpen(false)} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-[#151515] text-zinc-400 hover:text-white transition-colors text-sm font-medium">
              <Settings size={16} /> {t.userNav.settings}
            </Link>
          </div>

          <div className="h-px bg-[#1f1f1f] my-3" />

          <button onClick={logout} className="w-full flex items-center justify-center gap-2 p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors text-sm font-medium">
            <LogOut size={16} /> {t.userNav.logout}
          </button>
        </div>
      </div>
      
    </div>
  );
}